import { EventSource } from 'eventsource';
import mitt from 'mitt';

import * as SdkConfig from '../PleasedClient/client.config';
import { TeardownManager } from '../common/Teardown';
import type { EventHandler } from '../common/PleasedSdk.types';

/**
 * A SSE stream that will be created only when .subscribe
 * is called to listen to the stream.
 * `.close` method provides a clean way to destroy the sse.
 * It will also remove any resources attached via listeners.
 */
export class LazySseStream<T> {
  protected status: 'not-started' | 'running' | 'closed' = 'not-started';
  protected source: EventSource | null = null;
  protected events = mitt<{ data: T; close: void }>();
  protected $tdm = new TeardownManager();
  protected distinctSet = new Set();
  constructor(
    protected options: {
      url: string;
      eventName: string;
      distinct?: {
        kind: 'next-value-unique' | 'only-unique';
        by: (result: T) => string | number;
      };
    }
  ) {}

  /**
   * Check if we can emit the value, by checking the
   * distinct options. The distinct options provides
   * a way to only emit unique values.
   */
  protected canEmitValue(data: T) {
    if (this.status === 'closed') return false;

    const { distinct } = this.options;
    if (!distinct?.by) return true;
    const key = distinct.by(data) ?? 'no-key';
    const exists = this.distinctSet.has(key);
    if (exists) return false;

    distinct.kind === 'next-value-unique' && this.distinctSet.clear();
    this.distinctSet.add(key);
    return true;
  }

  protected create() {
    if (this.status !== 'not-started') return;
    const source = new EventSource(this.options.url, {
      fetch: (url, init) => {
        init.headers = {
          ...init.headers,
          'pl-access-token': SdkConfig.get('token') || '',
          'brand-subdomain': SdkConfig.get('brandCode') || '',
        };
        return fetch(url, init);
      },
    });
    const onMessage = (evt: MessageEvent<string>) => {
      const result: T = JSON.parse(evt.data);
      this.canEmitValue(result) && this.events.emit('data', result);
    };

    const {
      options: { eventName },
    } = this;

    source.addEventListener(eventName, onMessage);
    this.source = source;
    this.$tdm.add(() => {
      source.removeEventListener(eventName, onMessage);
      source.close();
    });

    this.$tdm.add(() => this.events.all.clear());

    this.status = 'running';
  }

  subscribe(fn?: EventHandler<T>) {
    if (this.status === 'closed') return this;
    fn && this.events.on('data', fn);
    if (this.status === 'not-started') this.create();
    return this;
  }

  close() {
    this.status = 'closed';
    this.events.emit('close');
    this.$tdm.teardown();
  }
}
